import { KEYS, bindFirst, createSlatePlugin, createTSlatePlugin, getInjectMatch } from "platejs";

//#region src/lib/BaseFontBackgroundColorPlugin.ts
const BaseFontBackgroundColorPlugin = createSlatePlugin({
	key: KEYS.backgroundColor,
	inject: { nodeProps: { nodeKey: "backgroundColor" } },
	parsers: { html: { deserializer: {
		isLeaf: true,
		rules: [{ validStyle: { backgroundColor: "*" } }],
		parse: ({ element, type }) => ({ [type]: element.style.backgroundColor })
	} } }
}).extendTransforms(({ editor }) => ({ addMark: (value) => {
	editor.tf.addMarks({ [KEYS.backgroundColor]: value });
} }));

//#endregion
//#region src/lib/BaseFontColorPlugin.ts
const BaseFontColorPlugin = createSlatePlugin({
	key: KEYS.color,
	inject: { nodeProps: {
		defaultNodeValue: "black",
		nodeKey: "color"
	} },
	parsers: { html: { deserializer: {
		isLeaf: true,
		rules: [{ validStyle: { color: "*" } }],
		parse({ element, type }) {
			if (element.style.color) return { [type]: element.style.color };
		}
	} } }
}).extendTransforms(({ editor }) => ({ addMark: (value) => {
	editor.tf.addMarks({ [KEYS.color]: value });
} }));

//#endregion
//#region src/lib/BaseFontFamilyPlugin.ts
const BaseFontFamilyPlugin = createSlatePlugin({
	key: KEYS.fontFamily,
	inject: { nodeProps: { nodeKey: "fontFamily" } },
	parsers: { html: { deserializer: {
		isLeaf: true,
		rules: [{ validStyle: { fontFamily: "*" } }],
		parse: ({ element, type }) => ({ [type]: element.style.fontFamily })
	} } }
}).extendTransforms(({ editor }) => ({ addMark: (value) => {
	editor.tf.addMarks({ [KEYS.fontFamily]: value });
} }));

//#endregion
//#region src/lib/BaseFontSizePlugin.ts
const BaseFontSizePlugin = createSlatePlugin({
	key: KEYS.fontSize,
	inject: { nodeProps: { nodeKey: "fontSize" } },
	parsers: { html: { deserializer: {
		isLeaf: true,
		rules: [{ validStyle: { fontSize: "*" } }],
		parse: ({ element, type }) => ({ [type]: element.style.fontSize })
	} } }
}).extendTransforms(({ editor }) => ({ addMark: (value) => {
	editor.tf.addMarks({ [KEYS.fontSize]: value });
} }));

//#endregion
//#region src/lib/BaseFontWeightPlugin.ts
const BaseFontWeightPlugin = createSlatePlugin({
	key: KEYS.fontWeight,
	inject: { nodeProps: { nodeKey: "fontWeight" } },
	parsers: { html: { deserializer: {
		isLeaf: true,
		rules: [{ validStyle: { fontWeight: "*" } }],
		parse: ({ element, type }) => ({ [type]: element.style.fontWeight })
	} } }
}).extendTransforms(({ editor }) => ({ addMark: (value) => {
	editor.tf.addMarks({ [KEYS.fontWeight]: value });
} }));

//#endregion
//#region src/lib/BaseTextAlignPlugin.ts
/** Creates a plugin that adds alignment functionality to the editor. */
const BaseTextAlignPlugin = createSlatePlugin({
	key: KEYS.textAlign,
	inject: {
		isBlock: true,
		nodeProps: {
			defaultNodeValue: "start",
			styleKey: "textAlign",
			validNodeValues: [
				"start",
				"left",
				"center",
				"right",
				"end",
				"justify"
			]
		},
		targetPlugins: [KEYS.p],
		targetPluginToInject: ({ editor }) => ({ parsers: { html: { deserializer: { parse: ({ element, node }) => {
			if (element.style.textAlign) node[editor.getType(KEYS.textAlign)] = element.style.textAlign;
		} } } } })
	},
	node: { type: "align" }
}).extendTransforms(({ editor }) => ({ setNodes: bindFirst(setAlign, editor) }));

//#endregion
//#region src/lib/transforms/setAlign.ts
const setAlign = (editor, value, setNodesOptions) => {
	const { defaultNodeValue, nodeKey } = editor.getInjectProps(BaseTextAlignPlugin);
	const match = getInjectMatch(editor, editor.getPlugin({ key: KEYS.textAlign }));
	if (value === defaultNodeValue) editor.tf.unsetNodes(nodeKey, {
		match,
		...setNodesOptions
	});
	else editor.tf.setNodes({ [nodeKey]: value }, {
		match,
		...setNodesOptions
	});
};

//#endregion
//#region src/lib/transforms/setLineHeight.ts
const setLineHeight = (editor, value, setNodesOptions) => {
	const { defaultNodeValue, nodeKey } = editor.getInjectProps(BaseLineHeightPlugin);
	const match = getInjectMatch(editor, editor.getPlugin({ key: KEYS.lineHeight }));
	if (value === defaultNodeValue) editor.tf.unsetNodes(nodeKey, {
		match,
		...setNodesOptions
	});
	else editor.tf.setNodes({ [nodeKey]: value }, {
		match,
		...setNodesOptions
	});
};

//#endregion
//#region src/lib/BaseLineHeightPlugin.ts
/**
* Enables support for text alignment, useful to align your content to left,
* right and center it.
*/
const BaseLineHeightPlugin = createSlatePlugin({
	key: KEYS.lineHeight,
	inject: {
		isBlock: true,
		nodeProps: {
			defaultNodeValue: 1.5,
			nodeKey: "lineHeight"
		},
		targetPlugins: [KEYS.p],
		targetPluginToInject: ({ editor, plugin }) => ({ parsers: { html: { deserializer: { parse: ({ element }) => {
			if (element.style.lineHeight) return { [editor.getType(plugin.key)]: element.style.lineHeight };
		} } } } })
	}
}).extendTransforms(({ editor }) => ({ setNodes: bindFirst(setLineHeight, editor) }));

//#endregion
//#region src/lib/BaseTextIndentPlugin.ts
const BaseTextIndentPlugin = createTSlatePlugin({
	key: KEYS.textIndent,
	inject: {
		isBlock: true,
		nodeProps: {
			nodeKey: "textIndent",
			styleKey: "textIndent",
			transformNodeValue({ getOptions, nodeValue }) {
				const { offset, unit } = getOptions();
				return nodeValue * offset + unit;
			}
		},
		targetPlugins: [KEYS.p]
	},
	options: {
		offset: 24,
		unit: "px"
	}
});

//#endregion
export { BaseTextAlignPlugin as a, BaseFontFamilyPlugin as c, setAlign as i, BaseFontColorPlugin as l, BaseLineHeightPlugin as n, BaseFontWeightPlugin as o, setLineHeight as r, BaseFontSizePlugin as s, BaseTextIndentPlugin as t, BaseFontBackgroundColorPlugin as u };